// ─────────────────────────────────────────────────────────────────
// Glass-i — Stage 6B.8A-3C: Simplified product creation constants.
//
// This file is the single source for the "Create Item" dropdown
// (Glass / Hardware / UPVC-Aluminium / Jobwork / Ecom) UX. It is the
// SIMPLE surface — the Advanced Product Creation Studio still owns
// all the full vocabularies + section toggles + image/SEO/BOM fields.
//
// Mapping policy:
//   • Every value below is mapped onto an EXISTING backend column
//     (core inventory_items or one of the 1:1 extension tables).
//   • The popup UX uses friendly labels — "Stock Name / Base Material"
//     instead of source_stock_item_id, "Selling Price" instead of
//     sales_rate, etc. The mapping happens in simpleCreateHelpers.
//   • Where the brief asks for a category label the backend enum does
//     NOT cover (e.g. EXTRA CLEAR, LACQUERED, FLUTED), we map to the
//     closest enum value AND preserve the original label in a free-
//     text companion field (glassColor / remarks) so the user can
//     still see / search the exact label.
//
// No DOM. No React. No axios. Pure data + helpers — unit-tested via
// node:test in __tests__/simpleCreate.test.js.
// ─────────────────────────────────────────────────────────────────

// ── Kind identifiers shared by the dropdown, modal switch and helper ──
export const SIMPLE_KINDS = Object.freeze({
  GLASS:     'glass',
  HARDWARE:  'hardware',
  PROFILE:   'profile',
  JOBWORK:   'jobwork',
  ECOMMERCE: 'ecommerce'
});

// Dropdown options (label + kind). The 6th option ("Advanced Product
// Studio") is intentionally NOT in this list — it's a navigation link
// in SimpleCreateMenu, not a popup.
export const SIMPLE_CREATE_OPTIONS = Object.freeze([
  { kind: SIMPLE_KINDS.GLASS,     label: 'Glass',                hint: 'Sheet, toughened, laminated, DGU, mirror' },
  { kind: SIMPLE_KINDS.HARDWARE,  label: 'Hardware / Accessory', hint: 'Handles, hinges, locks, patch fittings' },
  { kind: SIMPLE_KINDS.PROFILE,   label: 'UPVC / Aluminium',     hint: 'Profiles, sections, beadings' },
  { kind: SIMPLE_KINDS.JOBWORK,   label: 'Jobwork / Service',    hint: 'Toughening, cutting, polishing, installation' },
  { kind: SIMPLE_KINDS.ECOMMERCE, label: 'Ecom Product',         hint: 'Catalog item with slug + visibility' }
]);

// ── Per-kind backend mapping presets ──────────────────────────────
//
// The simple popup leaves most flags / classification fields at their
// natural defaults (so Advanced Studio can later refine them without
// the entry having been pinned to a misleading state). What the popup
// DOES set explicitly is captured here.

export const DEFAULT_FLAGS_BY_KIND = Object.freeze({
  [SIMPLE_KINDS.GLASS]: Object.freeze({
    itemType:         'glass',
    isGoods:          true,
    isService:        false,
    isStockItem:      true,
    isSellable:       true,
    isPurchasable:    true,
    isManufacturable: false
  }),
  [SIMPLE_KINDS.HARDWARE]: Object.freeze({
    itemType:         'finished_good',
    isGoods:          true,
    isService:        false,
    isStockItem:      true,
    isSellable:       true,
    isPurchasable:    true,
    isManufacturable: false
  }),
  [SIMPLE_KINDS.PROFILE]: Object.freeze({
    itemType:         'finished_good',
    isGoods:          true,
    isService:        false,
    isStockItem:      true,
    isSellable:       true,
    isPurchasable:    true,
    isManufacturable: false
  }),
  [SIMPLE_KINDS.JOBWORK]: Object.freeze({
    itemType:         'service',
    isGoods:          false,
    isService:        true,
    isStockItem:      false,
    isSellable:       true,
    isPurchasable:    false,
    isManufacturable: false,
    isJobworkItem:    true
  }),
  [SIMPLE_KINDS.ECOMMERCE]: Object.freeze({
    itemType:         'trading_good',
    isGoods:          true,
    isService:        false,
    isStockItem:      true,
    isSellable:       true,
    isPurchasable:    true,
    isManufacturable: false
  })
});

// ── Glass popup ───────────────────────────────────────────────────
// "Category" in the popup spec — these are the natural words a glass
// trader uses. Each maps onto a backend GLASS_FAMILIES enum value
// (closest semantic match). The original label is preserved in
// glassColor on the spec row, so reports can still distinguish e.g.
// CLEAR vs EXTRA CLEAR.
export const GLASS_CATEGORY_OPTIONS = Object.freeze([
  { label: 'CLEAR',       glassFamily: 'clear',      glassColor: 'CLEAR' },
  { label: 'TINTED',      glassFamily: 'tinted',     glassColor: 'TINTED' },
  { label: 'REFLECTIVE',  glassFamily: 'reflective', glassColor: 'REFLECTIVE' },
  { label: 'EXTRA CLEAR', glassFamily: 'clear',      glassColor: 'EXTRA CLEAR' },
  { label: 'MIRROR',      glassFamily: 'mirror',     glassColor: 'MIRROR' },
  { label: 'LACQUERED',   glassFamily: 'coated',     glassColor: 'LACQUERED' },
  { label: 'FLUTED',      glassFamily: 'frosted',    glassColor: 'FLUTED' },
  { label: 'SPECIAL',     glassFamily: 'other',      glassColor: 'SPECIAL' },
  { label: 'OTHER',       glassFamily: 'other',      glassColor: 'OTHER' }
]);

// Friendly Item Type options — maps directly to inventory_items.item_type.
export const ITEM_TYPE_OPTIONS = Object.freeze([
  { label: 'Glass',          value: 'glass' },
  { label: 'Raw Material',   value: 'raw_material' },
  { label: 'Consumable',     value: 'consumable' },
  { label: 'Service',        value: 'service' },
  { label: 'Finished Good',  value: 'finished_good' },
  { label: 'Trading Good',   value: 'trading_good' }
]);

// Friendly Taxability options — maps to inventory_items.taxability_type.
export const TAXABILITY_OPTIONS = Object.freeze([
  { label: 'Taxable',    value: 'taxable' },
  { label: 'Exempt',     value: 'exempt' },
  { label: 'Nil Rated',  value: 'nil_rated' },
  { label: 'Non GST',    value: 'non_gst' },
  { label: 'Zero Rated', value: 'zero_rated' }
]);

// ── Profile (UPVC / Aluminium) popup ──────────────────────────────
export const PROFILE_MATERIAL_OPTIONS = Object.freeze([
  { label: 'UPVC',         value: 'upvc' },
  { label: 'Aluminium',    value: 'aluminium' },
  { label: 'Slim Profile', value: 'aluminium', isSlimProfile: true },
  { label: 'Other',        value: 'other' }
]);

// ── Jobwork popup ─────────────────────────────────────────────────
// Maps user-friendly service names → backend jobworkType + jobworkCategory
// pair, so reports can still group by type / category later. The
// charge basis maps directly to chargeBasis (SQM/PCS/KG/MTR/Fixed).
export const JOBWORK_SERVICE_OPTIONS = Object.freeze([
  { label: 'Toughening',   jobworkType: 'glass_processing',  jobworkCategory: 'toughening' },
  { label: 'Lamination',   jobworkType: 'glass_processing',  jobworkCategory: 'lamination' },
  { label: 'DGU',          jobworkType: 'glass_processing',  jobworkCategory: 'dgu' },
  { label: 'Cutting',      jobworkType: 'glass_processing',  jobworkCategory: 'cutting' },
  { label: 'Polish',       jobworkType: 'glass_processing',  jobworkCategory: 'polishing' },
  { label: 'Drilling',     jobworkType: 'glass_processing',  jobworkCategory: 'drilling' },
  { label: 'Installation', jobworkType: 'installation',      jobworkCategory: 'installation' },
  { label: 'Transport',    jobworkType: 'transport',         jobworkCategory: 'transport' },
  { label: 'Labour',       jobworkType: 'labour',            jobworkCategory: 'labour' },
  { label: 'Other',        jobworkType: 'other',             jobworkCategory: 'other' }
]);

export const JOBWORK_CHARGE_BASIS_OPTIONS = Object.freeze([
  { label: 'SQM',   value: 'sqm',   rateBasis: 'per_sqm' },
  { label: 'PCS',   value: 'piece', rateBasis: 'per_piece' },
  { label: 'KG',    value: 'kg',    rateBasis: 'per_kg' },
  { label: 'MTR',   value: 'meter', rateBasis: 'per_meter' },
  { label: 'Fixed', value: 'fixed', rateBasis: 'fixed' }
]);

// ── Ecommerce popup ───────────────────────────────────────────────
export const ECOMMERCE_VISIBILITY_OPTIONS = Object.freeze([
  { label: 'Hidden',  value: 'hidden' },
  { label: 'Visible', value: 'public' }
]);

export const ECOMMERCE_PUBLISH_STATUS_OPTIONS = Object.freeze([
  { label: 'Draft',     value: 'draft' },
  { label: 'Published', value: 'published' }
]);

// ── Title / subtitle copy per kind (used by the modal headers) ────
export const KIND_HEADERS = Object.freeze({
  [SIMPLE_KINDS.GLASS]: Object.freeze({
    title:    'Create Glass Product',
    subtitle: 'Create simple glass products with stock, GST and pricing details.'
  }),
  [SIMPLE_KINDS.HARDWARE]: Object.freeze({
    title:    'Create Hardware / Accessory',
    subtitle: 'Add a finished hardware item — handles, hinges, locks, patch fittings.'
  }),
  [SIMPLE_KINDS.PROFILE]: Object.freeze({
    title:    'Create UPVC / Aluminium Profile',
    subtitle: 'Add a profile section — UPVC, aluminium or slim profile.'
  }),
  [SIMPLE_KINDS.JOBWORK]: Object.freeze({
    title:    'Create Jobwork / Service',
    subtitle: 'Add a chargeable service — toughening, cutting, polishing, installation.'
  }),
  [SIMPLE_KINDS.ECOMMERCE]: Object.freeze({
    title:    'Create Ecommerce Product',
    subtitle: 'Add a catalog item. Stays hidden + draft until you publish it.'
  })
});

export default {
  SIMPLE_KINDS,
  SIMPLE_CREATE_OPTIONS,
  DEFAULT_FLAGS_BY_KIND,
  GLASS_CATEGORY_OPTIONS,
  ITEM_TYPE_OPTIONS,
  TAXABILITY_OPTIONS,
  PROFILE_MATERIAL_OPTIONS,
  JOBWORK_SERVICE_OPTIONS,
  JOBWORK_CHARGE_BASIS_OPTIONS,
  ECOMMERCE_VISIBILITY_OPTIONS,
  ECOMMERCE_PUBLISH_STATUS_OPTIONS,
  KIND_HEADERS
};
